import { type ReactNode, type RefObject, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

import { MediaQuery } from '@common/hooks/useResponsive';
import { Header } from '@common/organisms/BaseModal/atoms/Header';

import { BaseModalType } from './BaseModalProvider';
import { useModalContext } from './useModalContext';

import styles from './BaseModal.module.scss';

export type ContextKeyBaseModalType = BaseModalType | string;

interface Props {
    children: ReactNode | ReactNode[];
    title: ReactNode;
    contextKey?: BaseModalType | string;
    className?: string;
    hidePadding?: boolean;
    render?: true;
    isDrawer?: boolean;
    icon?: ReactNode;
    hasBackdrop?: boolean;
    onCancel?: () => void;
    triggerRef?: RefObject<HTMLElement | null>;
}

export enum ModalActions {
    create = 'create',
    update = 'update',
}

export const baseModalSelector = 'base-modal';

export const BaseModal = ({
    children,
    title,
    contextKey = BaseModalType.Default,
    className = '',
    hidePadding,
    render,
    isDrawer,
    icon,
    hasBackdrop = true,
    triggerRef,
    onCancel,
}: Props) => {
    const { state, closeModal } = useModalContext(contextKey, isDrawer);
    const show = state?.show;

    const classNames = clsx(
        'Modal',
        'shown',
        hasBackdrop && styles['has-backdrop'],
        isDrawer && styles['drawer'],
        hidePadding && styles['hide--padding'],
        styles.modal,
        className,
    );

    useEffect(() => {
        const dialog = document.querySelector(`.${baseModalSelector}`) as HTMLDialogElement;
        const isMobile = window.matchMedia(`(max-width: ${MediaQuery.m}em)`).matches;

        if (hasBackdrop && state.show) {
            dialog.showModal();
        }
        if ((state.show && !state.drawer) || (state.show && isMobile) || (state.show && hasBackdrop)) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }

        if (state.drawer && !hasBackdrop && state.show) {
            document.body?.classList.toggle('container-with-open-drawer', true);

            return () => {
                document.body?.classList.remove('container-with-open-drawer');
            };
        }
        return undefined;
    }, [state.show, state.drawer, hasBackdrop]);

    const closeAndCancel = useCallback(() => {
        if (onCancel) {
            onCancel();
        }
        triggerRef?.current?.focus();
        closeModal();
    }, [closeModal, onCancel, triggerRef]);

    useEffect(() => {
        if (!show) return;
        const dialog = document.querySelector(`.${baseModalSelector}`) as HTMLDialogElement;
        if (hasBackdrop) {
            dialog.showModal();
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                closeAndCancel();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [show, closeAndCancel, hasBackdrop]);

    if (!show && !render) return null;

    const handleFormSubmit = (event: React.FormEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const onCloseModal = (target: HTMLElement) => {
        if (target.nodeName === 'DIALOG') {
            closeAndCancel();
        }
    };

    const modalContent = (
        <aside
            className={classNames}
            onSubmit={handleFormSubmit}
            onMouseDown={
                hasBackdrop
                    ? (e) => {
                          onCloseModal(e.target as HTMLElement);
                      }
                    : undefined
            }
            data-testid="base-modal"
        >
            <dialog className={baseModalSelector}>
                <div className={styles['dialog-content']}>
                    <Header onCancel={onCancel} icon={icon} title={title} closeModal={closeModal} />
                    <div className={styles['child-container']}>{children}</div>
                </div>
            </dialog>
        </aside>
    );

    // Use portal to render modal at document body level to avoid nested forms
    if (typeof document !== 'undefined') {
        return createPortal(modalContent, document.body);
    }

    return modalContent;
};
